// GeoJSON must be imported for the sitemapVersionLevel type definition
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { EMPTY_GUID } from "@iventis/utilities";
import { Status } from "@iventis/domain-model/model/status";
import { SitemapVersionLevelUploadMethod } from "@iventis/domain-model/model/sitemapVersionLevelUploadMethod";
import { SitemapVersionLevelStatus } from "@iventis/domain-model/model/sitemapVersionLevelStatus";
import { validateAreaGeometry } from "@iventis/map-export-import-mapper/src/layer-geojson-validation";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { ValidationError } from "@iventis/map-export-import-mapper/src/layer-geojson-validation-types";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { SiteMapNodeTypes, SitemapNode, SitemapVersionNode, SitemapVersionLevelNode, SitemapVersionLevelWithFiles, SitemapVersionWithFiles } from "./sitemap-types";

/**
 * Converts an array of sitemaps into an array of {@link SitemapNode}. To be primarily used inside the tree browser.
 * @param sitemaps - the sitemaps to be converted
 * @returns an array of SitemapNodes
 */
export function convertSitemapsToSitemapNodes<T extends Sitemap>(sitemaps: T[], rootId: string) {
    return sitemaps.map<SitemapNode>((sitemap) => ({
        id: sitemap.id,
        sourceId: sitemap.id,
        name: sitemap.name,
        sitemap,
        type: SiteMapNodeTypes.Sitemap,
        parentId: rootId,
        childCount: sitemap.versions.length,
        childNodes: sitemap.versions.map<SitemapVersionNode>((version) => ({
            id: version.id,
            sourceId: version.id,
            name: version.name,
            version,
            type: SiteMapNodeTypes.Version,
            childCount: version?.sitemapVersionLevels?.length,
            childNodes: sortByLevelIndex(version).map<SitemapVersionLevelNode>((level) => ({
                id: level.id,
                sourceId: level.id,
                name: level.levelName,
                level,
                type: SiteMapNodeTypes.Level,
                childCount: 0,
                childNodes: [],
            })), // Make all levels for this version be ordered by their level index
        })),
    }));
}

export const createEmptySitemap = (): Sitemap => ({
    id: EMPTY_GUID,
    name: "",
    colour: "#FF0000",
    projectId: EMPTY_GUID,
    tag: "",
    status: Status.Active,
    versions: [],
    mapOrder: null,
});

export const createEmptySitemapVersion = (sitemapId: string): SitemapVersion => ({
    id: EMPTY_GUID,
    date: undefined,
    name: "",
    sitemapVersionLevels: [],
    sitemapId,
    status: Status.Active,
    type: SitemapVersionType.TwoDimensional,
});

export const cadConverterVersions = [1, 2];

export const createEmptySitemapVersionLevel = (versionId: string, index = 0): SitemapVersionLevelWithFiles => ({
    id: EMPTY_GUID,
    levelIndex: index,
    levelName: "",
    levelAbbreviation: "",
    bounding: undefined,
    sitemapVersionId: versionId,
    status: SitemapVersionLevelStatus.Uploading,
    perimeter: undefined,
    uploadMethod: SitemapVersionLevelUploadMethod.Cad,
    error: "",
});

export const getNextLevelIndex = (sitemapVersionLevels: SitemapVersionLevel[]) => {
    let index = 0;
    sortByLevelIndex({ sitemapVersionLevels }).forEach((level) => {
        if (level.levelIndex - index === 0) {
            index += 1;
        }
    });
    return index;
};

export const hasSitemapChanged = (existingSitemap: Sitemap, sitemap: Sitemap) =>
    existingSitemap == null ||
    existingSitemap.name !== sitemap?.name ||
    existingSitemap.colour !== sitemap?.colour ||
    existingSitemap.tag !== sitemap?.tag ||
    existingSitemap.mapOrder !== sitemap?.mapOrder;

export const hasSitemapVersionLevelChanged = (oldLevel: SitemapVersionLevel, newLevel: SitemapVersionLevelWithFiles) => {
    // Check if basic info has changed
    if (oldLevel?.levelName !== newLevel?.levelName || oldLevel?.levelIndex !== newLevel?.levelIndex || oldLevel?.levelAbbreviation !== newLevel?.levelAbbreviation) {
        return true;
    }

    // Check if upload method has changed
    if (newLevel.uploadMethod !== oldLevel.uploadMethod) {
        return true;
    }

    // Check if file contents have changed
    if (newLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles && (newLevel.style != null || newLevel.mbtiles != null)) {
        return true;
    }
    if (newLevel.uploadMethod === SitemapVersionLevelUploadMethod.PMtiles && (newLevel.style != null || newLevel.pmtiles != null)) {
        return true;
    }
    if (newLevel.uploadMethod === SitemapVersionLevelUploadMethod.Cad && newLevel.cad != null) {
        return true;
    }

    // Check if perimeter has changed
    if ((typeof oldLevel?.perimeter === "string" ? oldLevel.perimeter : JSON.stringify(oldLevel?.perimeter ?? {})) !== newLevel.perimeter) {
        return true;
    }

    return false;
};

export const isSitemapValid = (sitemap: Sitemap) =>
    sitemap != null && sitemap.name != null && sitemap.name.length > 2 && sitemap.colour != null && sitemap.tag != null && sitemap.tag.length === 3;

export function isSitemapVersionLevelValid(sitemapVersionLevel: SitemapVersionLevelWithFiles, isNew: boolean) {
    if (sitemapVersionLevel == null) {
        return false;
    }

    const isBasicInfoValid = sitemapVersionLevel.levelName?.length > 0 && sitemapVersionLevel.levelAbbreviation?.length > 0 && sitemapVersionLevel.levelIndex != null;
    if (!isBasicInfoValid) {
        return false;
    }

    const isPerimeterValid =
        sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Cad ||
        validateAreaGeometry(parsePerimeter(sitemapVersionLevel.perimeter) ?? "", StyleType.Area) === ValidationError.None;
    if (!isPerimeterValid) {
        return false;
    }

    if (isNew) {
        const areFilesValid =
            sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles
                ? sitemapVersionLevel.mbtiles != null && sitemapVersionLevel.style != null
                : sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.PMtiles
                ? sitemapVersionLevel.pmtiles != null && sitemapVersionLevel.style != null
                : sitemapVersionLevel.cad != null;
        if (!areFilesValid) {
            return false;
        }
    }

    return true;
}

export const isSitemapVersionValid = (sitemapVersion: SitemapVersion) => sitemapVersion != null && sitemapVersion.name.length > 2;

export const hasSitemapVersionChanged = (oldSitemapVersion: SitemapVersion | SitemapVersionWithFiles, newSitemapVersion: SitemapVersion | SitemapVersionWithFiles) =>
    oldSitemapVersion == null || oldSitemapVersion.name !== newSitemapVersion.name;

export const sortByLevelIndex = <TVersion extends { sitemapVersionLevels: SitemapVersionLevel[] }>(sitemapVersion: TVersion): TVersion["sitemapVersionLevels"] =>
    sitemapVersion.sitemapVersionLevels?.sort((a, b) => (a.levelIndex < b.levelIndex ? -1 : a.levelIndex > b.levelIndex ? 1 : 0));

export function getSmallestAvailablePositiveIndex(sitemapVersionLevels: SitemapVersionLevel[]): number {
    if (!sitemapVersionLevels || sitemapVersionLevels.length === 0) return 0;
    const indices = Array.from(new Set(sitemapVersionLevels.map((l) => l.levelIndex).filter((i) => i >= 0))).sort((a, b) => a - b);
    let selectedIndex = 0;
    for (const idx of indices) {
        if (idx > selectedIndex) break;
        if (idx === selectedIndex) selectedIndex++;
    }
    return selectedIndex;
}

const parsePerimeter = (perimeter: string) => {
    try {
        return JSON.parse(perimeter);
    } catch {
        return null;
    }
};

/**
 * If a sitemap has a null mapOrder value, it is placed at the bottom of a map.
 * Sitemaps are ordered in the map from lowest (bottom of the map) order value to highest (top of the map)
 */
export function getSitemapMapOrderValue(mapOrder: Sitemap["mapOrder"]) {
    return mapOrder != null ? mapOrder : -1;
}

/**
 * Gets the latest sitemap version given an array of sitemap versions.
 * If there are no dates, the last sitemap version in the array is returned.
 * If there are no sitemap versions, null is returned.
 * @param sitemapVersions - the array of sitemap versions to check.
 * @returns the latest sitemap version, or null if there are no versions.
 */
export function getLatestSitemapVersion(sitemapVersions: SitemapVersion[]): SitemapVersion | null {
    // If there are no sitemap versions, return null
    if (!sitemapVersions || sitemapVersions.length === 0) return null;

    // If there are no dates, return the last sitemap version in the array
    if (sitemapVersions.every((version) => version.date == null)) {
        return sitemapVersions[sitemapVersions.length - 1];
    }

    // Otherwise return the sitemap version with the latest date
    const latestVersion = sitemapVersions.reduce((prev, current) => {
        const prevTime = prev.date != null ? new Date(prev.date).getTime() : -Infinity;
        const currentTime = current.date != null ? new Date(current.date).getTime() : -Infinity;
        return prevTime >= currentTime ? prev : current;
    });
    return latestVersion;
}
